import { Link } from 'react-router-dom';
import { Dumbbell, Target, Users, Award, Zap, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

const AboutUsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-20 px-6 md:px-12 lg:px-20 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/20 via-background to-background"></div>
        <div className="relative z-10 container mx-auto max-w-6xl">
          <div className="flex justify-end mb-4">
            <Link to="/">
              <Button variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white" data-testid="back-home-btn">
                Back to Home
              </Button>
            </Link>
          </div>
          <div className="text-center">
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_wholesome-health-1/artifacts/jxcg5bs9_WHOLESOME%20FITNESS.png" 
                alt="Wholesome Fitness Centre" 
                className="h-24 md:h-32 w-auto object-contain"
              />
            </div>
            <h1 className="text-5xl md:text-6xl font-heading font-black uppercase tracking-tight mb-6" data-testid="about-title">
              About Wholesome Fitness Centre
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Your premier destination for fitness, wellness, and transformation. Where technology meets dedication.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6 md:px-12 lg:px-20">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="bg-card/50 border-primary/30">
              <CardContent className="pt-8">
                <Target className="w-12 h-12 text-primary mb-4" />
                <h2 className="text-3xl font-heading font-bold uppercase mb-4">Our Mission</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To empower individuals to achieve their fitness goals through state-of-the-art facilities, 
                  innovative technology, and personalized support. We believe fitness should be accessible, 
                  enjoyable, and transformative for everyone.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-secondary/30">
              <CardContent className="pt-8">
                <Zap className="w-12 h-12 text-secondary mb-4" />
                <h2 className="text-3xl font-heading font-bold uppercase mb-4">Our Vision</h2>
                <p className="text-muted-foreground leading-relaxed">
                  To be Nigeria's leading fitness center that seamlessly integrates technology with wellness, 
                  creating a community where every member feels supported, motivated, and empowered to live 
                  their healthiest life.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-heading font-black uppercase text-center mb-12">Our Core Values</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase mb-3">Community</h3>
              <p className="text-muted-foreground">
                Building a supportive environment where members motivate and inspire each other.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-secondary/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-secondary" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase mb-3">Excellence</h3>
              <p className="text-muted-foreground">
                Providing world-class facilities, equipment, and services to ensure the best experience.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-10 h-10 text-primary" />
              </div>
              <h3 className="text-xl font-heading font-bold uppercase mb-3">Wellness</h3>
              <p className="text-muted-foreground">
                Focusing on holistic health - physical, mental, and emotional well-being.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 px-6 md:px-12 lg:px-20">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-heading font-black uppercase text-center mb-12">What Sets Us Apart</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-card/50 border-white/10 hover:border-primary/50 transition-colors">
              <CardContent className="pt-6">
                <h3 className="text-xl font-heading font-bold uppercase mb-3 text-primary">Smart Access Control</h3>
                <p className="text-muted-foreground">
                  Tuya-powered smart locks with QR codes, OTP, and temporary passwords for seamless, secure gym access.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/10 hover:border-primary/50 transition-colors">
              <CardContent className="pt-6">
                <h3 className="text-xl font-heading font-bold uppercase mb-3 text-primary">Digital Fitness Tracking</h3>
                <p className="text-muted-foreground">
                  Comprehensive workout logging, progress tracking, and analytics to help you achieve your goals faster.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/10 hover:border-primary/50 transition-colors">
              <CardContent className="pt-6">
                <h3 className="text-xl font-heading font-bold uppercase mb-3 text-primary">Premium Equipment</h3>
              <p className="text-muted-foreground">
                  State-of-the-art gym equipment and accessories available for purchase in our online store.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-card/50 border-white/10 hover:border-primary/50 transition-colors">
              <CardContent className="pt-6">
                <h3 className="text-xl font-heading font-bold uppercase mb-3 text-primary">Flexible Memberships</h3>
                <p className="text-muted-foreground">
                  Multiple subscription tiers with online payment processing and automatic renewal management.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-20 px-6 md:px-12 lg:px-20 bg-gradient-to-b from-transparent via-secondary/5 to-transparent">
        <div className="container mx-auto max-w-6xl">
          <h2 className="text-4xl font-heading font-black uppercase text-center mb-12">Our Facilities</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-card/50 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-heading font-bold uppercase mb-3">Cardio Zone</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Treadmills</li>
                <li>• Stationary Bikes</li>
                <li>• Rowing Machines</li>
                <li>• Ellipticals</li>
              </ul>
            </div>

            <div className="bg-card/50 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-heading font-bold uppercase mb-3">Strength Training</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Free Weights</li>
                <li>• Cable Machines</li>
                <li>• Smith Machines</li>
                <li>• Resistance Equipment</li>
              </ul>
            </div>

            <div className="bg-card/50 border border-white/10 rounded-xl p-6">
              <h3 className="text-lg font-heading font-bold uppercase mb-3">Amenities</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Locker Rooms</li>
                <li>• Showers</li>
                <li>• Sauna (Premium)</li>
                <li>• Group Class Studios</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 md:px-12 lg:px-20">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-black uppercase mb-6">Ready to Start Your Journey?</h2>
          <p className="text-xl text-muted-foreground mb-8">
            Join Wholesome Fitness Center today and experience the difference that technology and community make.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/register">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-bold uppercase px-8 py-6 text-lg">
                Get Started
              </Button>
            </Link>
            <Link to="/plans">
              <Button size="lg" variant="outline" className="border-2 border-secondary text-secondary hover:bg-secondary hover:text-black font-bold uppercase px-8 py-6 text-lg">
                View Membership Plans
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutUsPage;